import { AreaChart, LineChart } from '@mantine/charts'
import { formatCount, formatDateRange, formatDays } from '../format'
import type { TrendPoint } from '../hooks'
import { trendChartData, type TrendDatum } from '../utils'

function SliceTooltip({ datum, subject }: { datum: TrendDatum | undefined; subject: string }) {
  if (!datum) return null
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{formatDateRange(datum)}</div>
      <div className="text-slate-600">{subject}</div>
      <div className="mt-1 tabular-nums">
        {datum.closed === 0
          ? 'Nothing resolved'
          : `Median time to resolve ${formatDays(datum.medianDays)} (${formatCount(datum.closed)} resolved)`}
      </div>
    </div>
  )
}

/**
 * Median time to resolve over the period's slices (Mantine charts, like components/charts/
 * TrendLineChart.tsx). `sparkline` is the family cards' small line — no axes or grid, red when
 * the time to resolve grew — and `area` the trend row's filled chart. Both share the slice
 * tooltip, and both break the line at a slice where nothing resolved.
 */
export function ClosureTimeChart({
  points,
  subject,
  variant = 'area',
  worse = false,
}: {
  points: TrendPoint[]
  /** What the line is for — all families, a family group or a measure — for the tooltip. */
  subject: string
  variant?: 'area' | 'sparkline'
  /** The time to resolve grew against the comparison period: the sparkline turns red. */
  worse?: boolean
}) {
  if (variant === 'sparkline') {
    return (
      <LineChart
        h={44}
        data={trendChartData(points)}
        dataKey="label"
        series={[
          { name: 'days', label: 'Median time to resolve', color: worse ? 'red.6' : 'blue.6' },
        ]}
        curveType="monotone"
        connectNulls={false}
        strokeWidth={2}
        withDots
        dotProps={{ r: 2.5 }}
        withXAxis={false}
        withYAxis={false}
        gridAxis="none"
        yAxisProps={{ domain: ['dataMin - 5', 'dataMax + 5'] }}
        tooltipAnimationDuration={0}
        tooltipProps={{
          content: ({ payload }) => (
            <SliceTooltip
              datum={payload?.[0]?.payload as TrendDatum | undefined}
              subject={subject}
            />
          ),
        }}
      />
    )
  }

  return (
    <AreaChart
      h={220}
      data={trendChartData(points)}
      dataKey="label"
      series={[{ name: 'days', label: 'Median time to resolve', color: 'blue.6' }]}
      curveType="monotone"
      connectNulls={false}
      withGradient
      fillOpacity={0.25}
      strokeWidth={2.5}
      withDots
      gridAxis="none"
      tickLine="none"
      yAxisProps={{ domain: [0, 'auto'], width: 64 }}
      valueFormatter={(value) => `${Math.round(value)} days`}
      tooltipAnimationDuration={0}
      tooltipProps={{
        content: ({ payload }) => (
          <SliceTooltip datum={payload?.[0]?.payload as TrendDatum | undefined} subject={subject} />
        ),
      }}
    />
  )
}
